// ChannelNodeMentionAutocomplete.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// CSS
import './ChannelMentionAutocomplete.css';

// Icons
import Icon from 'svg/Icon';

/**
 * ChannelNodeMentionAutocomplete
 *
 * Renders # node-mention suggestions for the channel composer.
 *
 * @returns {ReactNode} The rendered component.
 */
const ChannelNodeMentionAutocomplete = React.memo(({
  candidates = [],
  activeIndex = 0,
  onSelect,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const { getType } = useNodeTypes();

  if (!candidates.length) return null;

  // ----- RENDER ----- //

  return (
    <div
      className="channel-mention-autocomplete"
      role="listbox"
      aria-label="Node mention suggestions"
    >
      {candidates.map((candidate, index) => {
        const isActive = index === activeIndex;
        const typeDef = getType?.(candidate.nodeType);
        const iconKeyValue = String(typeDef?.iconKey || '').trim() || 'deployed_code';

        return (
          <button
            className={`channel-mention-autocomplete-row${isActive ? ' is-active' : ''}`}
            style={{
              backgroundColor: isActive ? theme.input : 'transparent',
            }}
            key={candidate.nodeKey}
            type="button"
            role="option"
            aria-selected={isActive}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect?.(candidate);
            }}
          >

            {/* ICON */}
            <span className="channel-mention-autocomplete-avatar">
                <Icon
                  iconKey={iconKeyValue}
                  width={18}
                  height={18}
                  color={theme.foregroundMuted}
                />
            </span>

            {/* META */}
            <span className="channel-mention-autocomplete-meta">

              {/* TITLE */}
              <span className="channel-mention-autocomplete-name">
                {candidate.title}
              </span>

              {/* TYPE */}
              <span className="channel-mention-autocomplete-note">
                {candidate.typeLabel || candidate.nodeType}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
});

// Dev Tools
ChannelNodeMentionAutocomplete.displayName = 'ChannelNodeMentionAutocomplete';

export default ChannelNodeMentionAutocomplete;
